// The story board editor's agent panel (#1416, Adam 27 Sep): "if you click
// Ask the agent or anything else, the panel slides in from the right-hand
// side, exactly the same width as the one that we use for tables".
//
// It is the Insights drawer's shape: the same MUI Drawer sliding in from the
// right, the same width (INSIGHTS_DRAWER_WIDTH), and the same conversation
// surface (SidePaneChat: MessageRow rows, the one ComposerCard, a close
// button in its header). Two differences, both deliberate:
//  - it is persistent, not modal: no backdrop, so the page stays editable and
//    the agent's changes can be watched loading beside the conversation;
//  - it sits in the editor's page area, below the editor's bar, so Publish
//    and Close stay in reach while it is open.
//
// Presentational: the editor screen owns the conversation and the sending.
import React, { useMemo, useState } from 'react'
import { Alert, Box, Button, Drawer } from '@mui/material'
import { SidePaneChat, type AgentComposerChip, type ComposerModel, type PendingAttachment, type SidePaneMessage } from '../../agent'
import { INSIGHTS_DRAWER_WIDTH } from '../chat/ChatDrawer'
import type { StoryPanelRow } from './storyPanelRows'

export interface StoryAgentPanelProps {
  open: boolean
  onClose: () => void
  /** The cut-down conversation (storyPanelRows), images included. */
  rows: StoryPanelRow[]
  /** A turn is running. */
  working: boolean
  /** What the agent is doing, while it works. */
  workingLabel?: string
  onSend: (text: string, model: string) => boolean | void | Promise<boolean | void>
  models: ComposerModel[]
  selectedModel: string
  onModelChange: (id: string) => void
  /** The element the next message is about, as a composer chip. */
  chips: AgentComposerChip[]
  onRemoveChip: (id: string) => void
  notice?: string | null
  onDismissNotice?: () => void
  /** Why this person may not send, or undefined. */
  disabledReason?: string
  /** "Open full conversation": close the editor and show the whole session. */
  onOpenFullConversation: () => void
  /** File attach on the panel's composer. Absent: no paperclip. */
  attachments?: PendingAttachment[]
  onFilesSelected?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  /** Dictation: the customer-scoped /transcribe (buildTranscribeEndpoint) and the login it posts with. */
  transcribeEndpoint?: string
  authHeader?: string
}

// A row's `image` becomes an `<img>` in `extra`, cached on the row's own
// identity (storyPanelRows keeps that stable per message) so the conversion
// itself never breaks SidePaneChat's row memoisation.
const withImage = new WeakMap<StoryPanelRow, SidePaneMessage>()
function toSidePaneMessage(row: StoryPanelRow): SidePaneMessage {
  if (!row.image) return row
  let out = withImage.get(row)
  if (!out) {
    const { image, ...rest } = row
    out = {
      ...rest,
      extra: (
        <Box
          component="img"
          data-testid="story-panel-image"
          src={`data:${image.mimeType};base64,${image.base64}`}
          alt={image.name || 'An image the agent produced'}
          sx={{ display: 'block', maxWidth: '100%', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
        />
      ),
    }
    withImage.set(row, out)
  }
  return out
}

/**
 * The width the panel covers, as one CSS width: the editor gives this up on
 * the right of the page while the panel is open, so nothing sits under it.
 */
export const STORY_AGENT_PANEL_WIDTH =
  `clamp(${INSIGHTS_DRAWER_WIDTH.minWidth}px, ${INSIGHTS_DRAWER_WIDTH.width}, ${INSIGHTS_DRAWER_WIDTH.maxWidth}px)`

const reducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export default function StoryAgentPanel({
  open, onClose, rows, working, workingLabel, onSend, models, selectedModel, onModelChange,
  chips, onRemoveChip, notice, onDismissNotice, disabledReason, onOpenFullConversation,
  attachments, onFilesSelected, onRemoveAttachment, transcribeEndpoint, authHeader,
}: StoryAgentPanelProps) {
  // Bumped every time the panel finishes sliding in, so SidePaneChat scrolls
  // to the latest row on open rather than wherever it last sat while closed
  // (#1578 item 8).
  const [revealKey, setRevealKey] = useState(0)
  const messages = useMemo(() => rows.map(toSidePaneMessage), [rows])
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      transitionDuration={reducedMotion() ? 0 : undefined}
      SlideProps={{ onEntered: () => setRevealKey((n) => n + 1) }}
      data-testid="story-agent-panel"
      data-open={open ? 'true' : 'false'}
      sx={{
        '& .MuiDrawer-paper': {
          // Fills the editor's aside strip (below the bar, on the right),
          // which is STORY_AGENT_PANEL_WIDTH wide and clips the paper while
          // it slides out, so a closed panel cannot widen the page.
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          boxShadow: '-12px 0 32px rgba(38,55,87,0.10)',
        },
      }}
    >
      <SidePaneChat
        title="Agent"
        onClose={onClose}
        headerActions={(
          <Button
            size="small"
            color="info"
            data-testid="story-agent-full-conversation"
            onClick={onOpenFullConversation}
            sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            Open full conversation
          </Button>
        )}
        messages={messages}
        revealKey={revealKey}
        loading={working}
        loadingTitle={workingLabel || 'The agent is working…'}
        onSend={onSend}
        models={models}
        selectedModel={selectedModel}
        onModelChange={onModelChange}
        composerChips={chips}
        onRemoveComposerChip={onRemoveChip}
        attachments={attachments}
        onFilesSelected={onFilesSelected}
        onRemoveAttachment={onRemoveAttachment}
        transcribeEndpoint={transcribeEndpoint}
        authHeader={authHeader}
        aboveComposer={notice ? (
          <Alert severity="info" onClose={onDismissNotice} data-testid="story-agent-notice" sx={{ fontSize: 13 }}>
            {notice}
          </Alert>
        ) : undefined}
        placeholder={disabledReason || (chips.length ? 'What should change?' : 'Ask the agent about this story board…')}
        disabled={!!disabledReason}
      />
    </Drawer>
  )
}
